<template>
  <div>
    <!-- ACC-389: deep-link learner tried to self-enroll into a program-supported course. -->
    <ProgramSupportedModal
      :model-value="modalQueue.isCurrent('partner-pay-deeplink')"
      @update:model-value="(v) => !v && modalQueue.dismiss('partner-pay-deeplink')"
      @browse="goToPayToEnrollCourses"
    />
    <NotificationPanel />
    <!-- Full-screen notifications view -->
    <v-container v-if="notificationStore.panelOpen && notificationStore.panelExpanded">
      <NotificationFullScreen />
    </v-container>
    <v-container v-else-if="resultsFetched">
      <!-- Desktop toast banner (sm and up) -->
      <div
        v-if="toastDate && !toastDismissed"
        class="d-none d-sm-flex align-center"
        style="
          background-color: #ffffff;
          border: 1px solid #e8e8e8;
          box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
          padding: 24px;
          gap: 12px;
          margin-bottom: 24px;
        "
      >
        <img :src="calendarIcon" alt="Calendar" class="flex-shrink-0" style="width: 48px; height: 48px" />

        <div class="d-flex flex-column flex-grow-1" style="gap: 12px; min-width: 0">
          <!-- Title row: date + text + close -->
          <div class="d-flex align-center" style="width: 100%">
            <span
              class="flex-grow-1"
              style="
                font-family: Arial, sans-serif;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                letter-spacing: -0.48px;
                color: #000000;
              "
            >
              <span v-if="toastFormattedDate" style="color: #747474; font-weight: 700">{{ toastFormattedDate }} -</span>
              {{ toastDate.text }}
            </span>
            <button
              class="flex-shrink-0 ml-4"
              style="
                width: 20px;
                height: 20px;
                padding: 0;
                border: none;
                background: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
              "
              @click="dismissToast"
            >
              <v-icon size="16" color="#bfbfbf">mdi-close</v-icon>
            </button>
          </div>

          <!-- Body row: link + button -->
          <div class="d-flex align-center" style="width: 100%">
            <div class="flex-grow-1" style="min-width: 0">
              <button
                style="
                  font-family: Arial, sans-serif;
                  font-size: 14px;
                  line-height: 18px;
                  color: #000000;
                  text-decoration: underline;
                  background: none;
                  border: none;
                  padding: 0;
                  cursor: pointer;
                "
                @click="scrollToDates"
              >
                View more important dates
              </button>
            </div>
            <UiButton
              v-if="showToastAddCourses"
              variant="gold"
              size="small"
              class="flex-shrink-0"
              @click="$router.push({ name: 'addCourses' })"
            >
              Add courses
            </UiButton>
          </div>
        </div>
      </div>

      <!-- Mobile toast banner (xs only) -->
      <div
        v-if="toastDate && !toastDismissed"
        class="d-flex d-sm-none flex-column"
        style="
          background-color: #ffffff;
          border: 1px solid #e8e8e8;
          box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.16);
          padding: 24px;
          gap: 10px;
          margin-bottom: 16px;
        "
      >
        <!-- Title row: date + text + close -->
        <div class="d-flex align-start" style="width: 100%; gap: 8px">
          <span
            class="flex-grow-1"
            style="
              font-family: Arial, sans-serif;
              font-weight: 700;
              font-size: 16px;
              line-height: 24px;
              letter-spacing: -0.48px;
              color: #000000;
            "
          >
            <span v-if="toastFormattedDate" style="color: #747474; font-weight: 700">{{ toastFormattedDate }} -</span>
            {{ toastDate.text }}
          </span>
          <button
            class="flex-shrink-0"
            style="
              width: 20px;
              height: 20px;
              padding: 0;
              border: none;
              background: none;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
            "
            @click="dismissToast"
          >
            <v-icon size="16" color="#bfbfbf">mdi-close</v-icon>
          </button>
        </div>

        <!-- Calendar icon -->
        <div class="d-flex justify-center">
          <img :src="calendarIcon" alt="Calendar" style="width: 48px; height: 48px" />
        </div>

        <!-- Add courses button (full width, session signup only) -->
        <UiButton
          v-if="showToastAddCourses"
          variant="gold"
          size="small"
          block
          @click="$router.push({ name: 'addCourses' })"
        >
          Add courses
        </UiButton>

        <!-- View more link -->
        <button
          style="
            font-family: Arial, sans-serif;
            font-size: 14px;
            line-height: 18px;
            color: #000000;
            text-decoration: underline;
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            text-align: center;
            width: 100%;
          "
          @click="scrollToDates"
        >
          View more important dates
        </button>
      </div>

      <div class="dashboard-divider" :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-12'">
        <div style="display: flex; justify-content: space-between; align-items: center">
          <div style="display: flex; align-items: center">
            <div class="font-weight-bold" :class="$vuetify.display.smAndDown ? 'text-h5' : 'text-h4'">
              {{ learnerWelcomeMessage }} {{ (basicLearnerInfo || {}).name
              }}<span>
                <img
                  v-if="isIdVerified"
                  class="ml-2"
                  :style="$vuetify.display.smAndDown ? 'width: 16px' : 'width: 24px;'"
                  :src="badgeCheckBlueIcon"
                  alt="Verified User Badge"
              /></span>
            </div>
          </div>

          <DashboardNotificationDropdown />
        </div>
        <div
          style="color: #747474"
          :class="$vuetify.display.smAndDown ? 'pt-3 pb-10 text-body-2' : 'pt-4 pb-6 text-body-1'"
        >
          Here you’ll find everything you need for your learning journey
        </div>

        <v-row wrap>
          <div class="d-flex d-sm-none sticky-anchors" id="myHeader">
            <a
              @click="
                anchorValue = 1;
                scrollToId('progress');
              "
              :style="anchorValue == 1 ? 'color : #2a2a2a' : ''"
              >Progress</a
            >
            <a
              @click="
                anchorValue = 2;
                scrollToId('credits');
              "
              :style="anchorValue == 2 ? 'color : #2a2a2a' : ''"
              >Credits</a
            >

            <a
              @click="
                anchorValue = 4;
                scrollToId('support');
              "
              :style="anchorValue == 4 ? 'color : #2a2a2a' : ''"
              >Support</a
            >
            <a
              @click="
                anchorValue = 5;
                scrollToId('resources');
              "
              :style="anchorValue == 5 ? 'color : #2a2a2a' : ''"
              >Resources</a
            >
          </div>
          <div class="mobile-anchor-border d-flex d-sm-none" />
        </v-row>
      </div>

      <verification-status id="idVerificationBox" />

      <!-- Courses Panel - Full Width -->
      <div id="credits" :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-10'">
        <DashboardCoursesPanel :course_sections="course_sections" @course-unenrolled="handleCourseUnenrolled" />
      </div>

      <!-- Degrees and certificates card -->
      <DashboardDegreesCertificates
        id="progress"
        :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-10'"
        :progressCertificates="certificatesData"
      />

      <!-- Calendar (2/3) + Complete Profile (1/3) - when profile incomplete -->
      <!-- Calendar (2/3) + Have Questions (1/3) - when profile complete -->
      <v-row no-gutters :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-10'">
        <v-col d-flex cols="12" md="8" lg="8" xl="8" :class="$vuetify.display.smAndDown ? 'mb-6' : ''">
          <DashboardCalendarCard id="calendar" :class="$vuetify.display.mdAndUp ? 'mr-6' : ''" style="height: 100%" />
        </v-col>
        <v-col d-flex cols="12" md="4" lg="4" xl="4">
          <DashboardCompleteProfileCard
            v-if="!educationalInfo.highSchoolExpectedGraduationDate"
            :expectedGraduationDate="educationalInfo.highSchoolExpectedGraduationDate"
            style="height: 100%"
          />
          <MySupportCard v-else id="support" style="width: 100%; height: 100%" />
        </v-col>
      </v-row>

      <!-- Financial Wellness (2/3) + Have Questions (1/3) - when profile incomplete -->
      <!-- Financial Wellness (full width) - when profile complete -->
      <v-row no-gutters :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-10'">
        <v-col
          d-flex
          cols="12"
          :md="!educationalInfo.highSchoolExpectedGraduationDate ? 8 : 12"
          :lg="!educationalInfo.highSchoolExpectedGraduationDate ? 8 : 12"
          :xl="!educationalInfo.highSchoolExpectedGraduationDate ? 8 : 12"
          :class="!educationalInfo.highSchoolExpectedGraduationDate ? ($vuetify.display.mdAndUp ? 'pr-6' : 'mb-6') : ''"
        >
          <FinancialWellnessCard :compact="!educationalInfo.highSchoolExpectedGraduationDate" style="width: 100%" />
        </v-col>
        <v-col v-if="!educationalInfo.highSchoolExpectedGraduationDate" d-flex cols="12" md="4" lg="4" xl="4">
          <MySupportCard id="support" style="width: 100%; height: 100%" />
        </v-col>
      </v-row>

      <!-- Stay Engaged (1/2) + Additional Resources (1/2) -->
      <v-row no-gutters :class="$vuetify.display.smAndDown ? 'mb-6' : 'mb-10'">
        <v-col d-flex cols="12" md="6" lg="6" xl="6" :class="$vuetify.display.smAndDown ? 'mb-6' : ''">
          <DashboardStayEngagedCard :class="$vuetify.display.mdAndUp ? 'mr-6' : ''" style="height: 100%" />
        </v-col>
        <v-col d-flex cols="12" md="6" lg="6" xl="6">
          <DashboardAdditionalResources style="height: 100%" id="resources" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { useUserStore } from '@/stores/user';
import { useTour } from '@/tour';
import { useCourseDates } from '@/composables/useCourseDates';
import { useSessionMilestones } from '@/composables/useSessionMilestones';
import { todayDateStr } from '@/utils/dateUtils';
import badgeCheckBlueIcon from '@/assets/icons/badge-check-blue.svg';
import calendarIcon from '@/assets/images/dashboard-dates-to-remember-calendar.svg';

import DashboardDegreesCertificates from '../components/accelerate/DashboardDegreesCertificates.vue';
import { SessionInitializer } from '../mixins/sessionInitializer';
import SectionGetStarted from '../components/accelerate/SectionGetStarted.vue';
import DashboardCoursesPanel from '../components/courses/DashboardCoursesPanel.vue';
import DashboardCompleteProfileCard from '../components/accelerate/DashboardCompleteProfileCard.vue';
import DashboardCalendarCard from '../components/accelerate/DashboardCalendarCard.vue';
import MySupportCard from '../components/accelerate/MySupportCard.vue';
import DashboardAdditionalResources from '../components/accelerate/DashboardAdditionalResources.vue';
import DashboardStayEngagedCard from '../components/accelerate/DashboardStayEngagedCard.vue';
import DashboardNotificationDropdown from '../components/accelerate/DashboardNotificationDropdown.vue';
import NotificationPanel from '../components/accelerate/NotificationPanel.vue';
import NotificationFullScreen from '../components/accelerate/NotificationFullScreen.vue';
import FinancialWellnessCard from '../components/accelerate/FinancialWellnessCard.vue';
import UiButton from '../components/ui/Button.vue';
import ProgramSupportedModal from '../components/ProgramSupportedModal.vue';
import { useNotificationStore } from '@/stores/notifications';
import { useModalQueueStore } from '@/stores/modalQueue';
import moment from 'moment-timezone';
import { DataLayerMixin } from '../mixins/dataLayerMixin';

export default defineComponent({
  mixins: [SessionInitializer, DataLayerMixin],
  setup() {
    const { latestUpcomingDate, formatDateShort } = useCourseDates();
    const { sessionMilestoneEvents } = useSessionMilestones();
    const notificationStore = useNotificationStore();
    const modalQueue = useModalQueueStore();
    return { latestUpcomingDate, formatDateShort, sessionMilestoneEvents, calendarIcon, notificationStore, modalQueue };
  },
  components: {
    ProgramSupportedModal,
    DashboardDegreesCertificates,
    SectionGetStarted,
    DashboardCoursesPanel,
    DashboardCompleteProfileCard,
    DashboardCalendarCard,
    MySupportCard,
    DashboardAdditionalResources,
    DashboardStayEngagedCard,
    DashboardNotificationDropdown,
    NotificationPanel,
    NotificationFullScreen,
    FinancialWellnessCard,
    UiButton
  },
  props: {
    refreshDashboardPage: {
      type: Object
    },
    // The page load DashboardWrapper runs; this view renders it rather than loading again.
    pageLoad: {
      type: Promise,
      default: null
    }
  },
  watch: {
    'refreshDashboardPage.dashboard': {
      async handler() {
        this.resultsFetched = false;
        this.getEnrollments(async () => {
          this.earnedCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsCompleted;
          this.totalCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsNeeded;
          [this.basicLearnerInfo, this.educationalInfo] = await Promise.all([
            this.$conn.getBasicLearnerInfo(),
            this.$conn.getEducationalInformation()
          ]);
          this.resultsFetched = true;

          this.notificationStore.loadReadState();
          this.notificationStore.setNotifications(
            this.userStore.apis.learnerData._studentAnnouncements,
            ((this.$conn.partnerData || {}).dashboardPageData || {}).studentPortalAnnouncement
          );
        });
        this.getBasicAndDemograhicInfo();
      },
      immediate: false
    }
  },
  data() {
    return {
      basicLearnerInfo: null,
      educationalInfo: {},
      earnedCredits: null,
      totalCredits: null,
      course_sections: [],
      resultsFetched: false,
      anchorValue: 1,
      badgeCheckBlueIcon,
      toastDismissedLocal: false
    };
  },
  computed: {
    coursesStore: () => useCoursesStore(),
    userStore: () => useUserStore(),
    showDegreesAndCertificates() {
      return this.coursesStore.showLearnerProgress;
    },
    learnerWelcomeMessage() {
      return this.checkIfExistingLearner(this.userStore.apis.learnerData._firstPortalSignIn) ? 'Welcome back,' : 'Hi,';
    },
    isIdVerified() {
      return this.userStore.apis.learnerData._idVerify === 'approved';
    },
    certificatesData() {
      return this.coursesStore.certificatesAndMastery;
    },
    toastDate() {
      const today = todayDateStr();
      const upcomingMilestones = (this.sessionMilestoneEvents || []).filter((e) => e.date >= today);
      const soonestMilestone = upcomingMilestones[0] || null;
      if (!this.latestUpcomingDate && !soonestMilestone) return null;
      if (!this.latestUpcomingDate) return soonestMilestone;
      if (!soonestMilestone) return this.latestUpcomingDate;
      return new Date(this.latestUpcomingDate.date) <= new Date(soonestMilestone.date)
        ? this.latestUpcomingDate
        : soonestMilestone;
    },
    showToastAddCourses() {
      return this.toastDate?.type === 'session_milestone' && this.toastDate?.id?.startsWith('session-start-');
    },
    toastFormattedDate() {
      if (!this.toastDate) return '';
      return this.formatDateShort(this.toastDate.date);
    },
    toastDismissKey() {
      if (!this.toastDate) return '';
      return `toast_dismissed_${this.toastDate.date}_${this.toastDate.id}`;
    },
    toastDismissed() {
      if (this.toastDismissedLocal) return true;
      if (!this.toastDismissKey) return false;
      try {
        return localStorage.getItem(this.toastDismissKey) === 'true';
      } catch {
        return false;
      }
    }
  },
  async created() {
    window.addEventListener('scroll', this.stickyHeaderOnScroll);
    await this.awaitPageLoad(this.pageLoad, 'dashboard', async () => {
      this.earnedCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsCompleted;
      this.totalCredits = this.$conn.learnerData.dashboardPageData.numberOfCreditsNeeded;
      [this.basicLearnerInfo, this.educationalInfo] = await Promise.all([
        this.$conn.getBasicLearnerInfo(),
        this.$conn.getEducationalInformation()
      ]);
      this.resultsFetched = true;

      this.notificationStore.loadReadState();
      this.notificationStore.setNotifications(
        this.userStore.apis.learnerData._studentAnnouncements,
        ((this.$conn.partnerData || {}).dashboardPageData || {}).studentPortalAnnouncement
      );

      // ACC-408: the Announcements tour joins the modal queue as the lowest-priority
      // item (10), so it waits for every other auto-modal. Its config/schedule/seen/
      // View-As gates are the canShow; App.vue launches it once it becomes current.
      this.modalQueue.requestShow({
        id: 'product-tour',
        priority: 10,
        canShow: () => useTour().isAutoStartEligible('announcements')
      });

      // ACC-389: gate a program-supported (PartnerPay) deep-link with the popup.
      await this.processDeeplinkSelfEnrollGuard();
    });
  },
  methods: {
    /**
     * ACC-389: When the learner arrives via a course_offering_id deep-link, check whether
     * they may self-enroll. PartnerPay (program-supported) courses are partner-enrolled
     * only, so show the popup instead. Fails open (the backend POST /api/enrollments guard
     * is the authoritative backstop).
     */
    async processDeeplinkSelfEnrollGuard() {
      const courseOfferingId = this.$route.query.course_offering_id;
      if (!courseOfferingId) return;

      const eligibility = await this.checkSelfEnrollEligibility(courseOfferingId);
      if (!eligibility.allowed) {
        this.showProgramSupportedDeeplinkBlock();
      }
    },

    async checkSelfEnrollEligibility(courseOfferingId) {
      try {
        const learnerId = this.$conn.learnerData?._learnerId || this.$conn.learnerData?._learnerApplicationId;
        if (!learnerId || !courseOfferingId) return { allowed: true, reason: null };

        const apiPath = `/api/enrollments/self-enroll-eligibility?learnerId=${encodeURIComponent(
          learnerId
        )}&courseOfferingId=${encodeURIComponent(courseOfferingId)}`;

        const response = await this.$conn._doConnect(apiPath, 'get', this.$conn.learnerData.basicLearnerInformation);

        // _doConnect returns the axios response; the body may be the payload directly
        // or the raw Lambda envelope ({ statusCode, body: '<json>' }).
        let payload = response?.data;
        if (payload && typeof payload.body === 'string') {
          try {
            payload = JSON.parse(payload.body);
          } catch (e) {
            payload = response?.data;
          }
        }

        if (payload && typeof payload.allowed === 'boolean') return payload;
        return { allowed: true, reason: null };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Self-enroll eligibility check failed (failing open):', error);
        return { allowed: true, reason: null };
      }
    },

    showProgramSupportedDeeplinkBlock() {
      if (this.$route.query.course_offering_id) {
        const query = { ...this.$route.query };
        delete query.course_offering_id;
        this.$router.replace({ query });
      }
      // ACC-408: highest non-blocker priority (90) — honors explicit user intent
      // (the learner followed a course deep-link), so it shows ahead of any nag.
      this.modalQueue.requestShow({ id: 'partner-pay-deeplink', priority: 90 });
    },

    goToPayToEnrollCourses() {
      this.modalQueue.dismiss('partner-pay-deeplink');
      this.$router.push({ path: '/courses-add' });
    },

    handleCourseUnenrolled(updatedSections) {
      // Immediately update the course_sections data to reflect the unenrollment
      this.course_sections = updatedSections;
    },
    checkIfExistingLearner(portal_sign_in_date) {
      return portal_sign_in_date ? moment(portal_sign_in_date).isBefore(moment(), 'day') : false;
    },
    stickyHeaderOnScroll() {
      const header = document.getElementById('myHeader');
      if (header) {
        const sticky = header.offsetTop + 40;
        if (window.pageYOffset > sticky) {
          header.classList.add('sticky');
        } else {
          header.classList.remove('sticky');
        }
      }
    },
    dismissToast() {
      this.asuoDataLayerClick('accelerateDashboard', '30');
      this.toastDismissedLocal = true;
      try {
        localStorage.setItem(this.toastDismissKey, 'true');
      } catch {
        // localStorage unavailable
      }
    },
    scrollToDates() {
      this.asuoDataLayerClick('accelerateDashboard', '29');
      const el = document.getElementById('calendar');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    scrollToId(id) {
      this.asuoDataLayerClick('accelerateDashboard', '21', { text: id });
      const element = document.getElementById(id);
      if (element) {
        const offset = id === 'resources' ? 90 : id === 'progress' ? 80 : 60;
        window.scrollTo({ top: element.offsetTop - offset, behavior: 'smooth' });
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.dashboard-divider {
  border-bottom: 1px solid #e2e2e2;
}

.mobile-anchor-border {
  width: 100%;
  border-bottom: 2px solid #dddddd;
}

.sticky-anchors {
  z-index: 1;
  width: 100%;
  padding-bottom: 15px;
  background-color: white;
  text-align: center;

  a {
    color: #828282;
    padding: 14.5px 10px;
    font-size: 12px;
    font-weight: bold;
  }
}

@media only screen and (max-width: 320px) {
  .sticky-anchors {
    a {
      font-size: 10px;
    }
  }
}
</style>
